import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'material-react-toastify';
import { DateTime } from 'luxon';
import { TranslatedText } from '../../interfaces/Translation';
import {
  getLastModifiedLocale,
  setLastModifiedLocale,
} from '../../utils/Translation';
import { LanguageDropdown } from '../Dropdowns/LanguageDropdown';
import { sharedStyles } from '../../utils/Style';
import {
  RichTextEditor,
  RichTextEditorProps,
} from '../RichTextEditor/RichTextEditor';
import { translateContent } from '../../api/Translation';

type TranslatedTextDialogProps = {
  defaultValue: string;
  value: TranslatedText;
  open: boolean;
  onClose: () => void;
  onTranslate: (value: TranslatedText) => void;
  multiline?: boolean;
  richText?: boolean;
  richTextProps?: Partial<RichTextEditorProps>;
  width?: number;
};

export const TranslatedTextDialog = ({
  defaultValue,
  value: initialValue,
  open,
  onClose,
  onTranslate,
  multiline = false,
  richText = false,
  richTextProps,
  width = 450,
}: TranslatedTextDialogProps) => {
  const fallbackLocale = getLastModifiedLocale();

  const [selectedLocale, setSelectedLocale] = useState(fallbackLocale);
  const [value, setValue] = useState<TranslatedText>({});
  const [translating, setTranslating] = useState(false);
  const [translatedAt, setTranslatedAt] = useState(DateTime.now().toISO());

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, open]);

  const handleSelectLocale = useCallback((locale: string | null) => {
    setSelectedLocale(locale || fallbackLocale);
    setLastModifiedLocale(locale || fallbackLocale);
  }, []);

  const handleTranslate = () => {
    setTranslating(true);
    translateContent(defaultValue, selectedLocale)
      .then((translated) => {
        setValue({ ...value, [selectedLocale]: translated });
        setTranslatedAt(DateTime.now().toISO());
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setTranslating(false));
  };

  const handleChange = useCallback(
    (x: string) => {
      setValue({ ...value, [selectedLocale]: x });
    },
    [value, selectedLocale],
  );

  const handleSave = useCallback(() => {
    onTranslate(value);
    onClose();
  }, [onClose, onTranslate, value]);

  const resolvedValue = value[selectedLocale] ?? '';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg'>
      <DialogTitle>Lokalizasyon</DialogTitle>

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          width,
          minHeight: richText ? 300 : undefined,
        }}
      >
        <Box
          sx={{
            mt: 0.75,
            display: 'flex',
            flexDirection: 'row',
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <LanguageDropdown
            label='Dil'
            value={selectedLocale}
            onChange={handleSelectLocale}
          />

          <Button
            variant='outlined'
            startIcon={<AutoAwesomeIcon />}
            onClick={handleTranslate}
            sx={{ ...sharedStyles.buttonText }}
          >
            Çevir
          </Button>

          {translating && <CircularProgress size={24} />}
        </Box>

        {richText ? (
          <RichTextEditor
            key={selectedLocale + translatedAt}
            placeholder=''
            initialData={resolvedValue}
            onChange={(x) => handleChange(x)}
            width={width}
            {...richTextProps}
          />
        ) : (
          <TextField
            size='small'
            label='Değer'
            variant='outlined'
            multiline={multiline}
            placeholder={defaultValue}
            fullWidth
            value={resolvedValue}
            onChange={(e) => handleChange(e.target.value)}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 1.5 }}>
        <Button
          variant='contained'
          color='inherit'
          onClick={onClose}
          sx={sharedStyles.buttonText}
        >
          İptal
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={handleSave}
          sx={sharedStyles.buttonText}
        >
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};
